import { priceTiers } from '@/mocks/products';

interface PriceTierSectionProps {
  activeTier: string;
  onTierChange: (tier: string) => void;
}

const tierImages: Record<string, string> = {
  single:
    'https://public.readdy.ai/ai/img_res/eb019f472d49d4cc339902d133d6ceb0.jpg',
  set: 'https://readdy.ai/api/search-image?query=premium%20raw%20meat%20combination%20set%20assorted%20pork%20beef%20chicken%20arranged%20beautifully%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20generous%20BBQ%20party%20set%20presentation&width=600&height=400&seq=tier002&orientation=landscape',
  premium:
    'https://readdy.ai/api/search-image?query=Japanese%20A5%20wagyu%20beef%20Iberico%20pork%20luxury%20premium%20meat%20selection%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20high%20end%20gourmet%20gift%20box%20presentation&width=600&height=400&seq=tier003&orientation=landscape',
};

const tierColors: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  all: {
    bg: 'bg-[#FAF8F5]',
    border: 'border-[#D4A96A]',
    text: 'text-[#8B4513]',
    badge: 'bg-[#8B4513] text-white',
  },
  single: {
    bg: 'bg-[#FFF9F0]',
    border: 'border-[#D4A96A]',
    text: 'text-[#8B4513]',
    badge: 'bg-[#D4A96A] text-white',
  },
  set: {
    bg: 'bg-[#FFF5F0]',
    border: 'border-[#C0392B]',
    text: 'text-[#C0392B]',
    badge: 'bg-[#C0392B] text-white',
  },
  premium: {
    bg: 'bg-[#1a1a1a]',
    border: 'border-[#D4A96A]',
    text: 'text-[#D4A96A]',
    badge: 'bg-[#D4A96A] text-[#1a1a1a]',
  },
};

export default function PriceTierSection({ activeTier, onTierChange }: PriceTierSectionProps) {
  const handleTierClick = (tierId: string) => {
    onTierChange(tierId);
    const el = document.getElementById('products');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="px-6 lg:px-16">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.35em] text-[#8B4513] mb-3 font-medium">
            Price Tier
          </p>
          <h3 className="text-4xl lg:text-5xl font-black text-[#1a1a1a] tracking-tight mb-4">
            選擇您的價格帶
          </h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
            從日常單品到頂級禮盒，每個價格帶都有嚴選好肉，滿足不同場合的需求
          </p>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {priceTiers.filter((t) => t.id !== 'all').map((tier) => {
            const colors = tierColors[tier.id];
            const isActive = activeTier === tier.id;
            const isPremium = tier.id === 'premium';

            return (
              <button
                key={tier.id}
                onClick={() => handleTierClick(tier.id)}
                className={`relative group text-left rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer border-2 ${
                  isActive ? colors.border : 'border-transparent'
                } ${isPremium ? 'bg-[#1a1a1a]' : 'bg-[#FAF8F5]'} hover:scale-[1.02]`}
              >
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={tierImages[tier.id]}
                    alt={tier.label}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div
                    className={`absolute inset-0 ${
                      isPremium
                        ? 'bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a]/40 to-transparent'
                        : 'bg-gradient-to-t from-black/50 via-black/10 to-transparent'
                    }`}
                  />
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute top-3 right-3 w-7 h-7 bg-white rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-[#8B4513] text-sm font-bold"></i>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className={`p-6 ${isPremium ? 'bg-[#1a1a1a]' : 'bg-white'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${colors.badge}`}
                        >
                          {tier.sublabel}
                        </span>
                      </div>
                      <h4
                        className={`text-2xl font-black tracking-tight ${
                          isPremium ? 'text-white' : 'text-[#1a1a1a]'
                        }`}
                      >
                        {tier.label}
                      </h4>
                    </div>
                    <span
                      className={`w-10 h-10 flex items-center justify-center rounded-full ${
                        isPremium ? 'bg-[#D4A96A]/20' : 'bg-[#8B4513]/10'
                      }`}
                    >
                      <i className={`${tier.icon} text-xl ${colors.text}`}></i>
                    </span>
                  </div>

                  <p className={`text-sm font-semibold mb-4 ${colors.text}`}>{tier.range}</p>

                  <div
                    className={`flex items-center text-xs font-medium gap-1 ${
                      isPremium ? 'text-[#D4A96A]/70' : 'text-gray-400'
                    } group-hover:gap-2 transition-all`}
                  >
                    <span>立即選購</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
